import { useMemo } from 'react';
import { formatDate } from '@utils/dateHelpers';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@root/globalTypes';
import { queryKeys } from '@utils/consts';
import { queryFetch } from '@services/api';
import { WorkloadCalendarRespSuccess } from './types';
import { useQueryParams } from '@hooks/queryParams';
import { getFirstDayOfBusinessWeek } from '@contexts/workload/constants';
import { computeWeeklyDrafts, filterWorkloadWeeklyRecords } from './hiddenRecords';
import { useWorkloadFilterParams } from '@hooks/workload/filterParams';

export const useGetWorkloadCalendar = (date: Date, weekDates?: Date[]) => {
  const { params, parseParams } = useQueryParams();

  const {
    type,
    search,
    unassigned,
    sortingValue,
    sortingDirection,
    secondarySortingValue,
    secondarySortingDirection,
  } = params;

  const queryParams = parseParams(
    {
      date: type !== 'day' ? formatDate(getFirstDayOfBusinessWeek(date)) : formatDate(date),
      search,
      unassigned,
      sortingValue,
      sortingDirection,
      secondarySortingValue,
      secondarySortingDirection,
      calendarType: type,
    },
    { arrayFormat: 'bracket' },
  );

  const { data, error, isPending, isPlaceholderData, isFetching } = useQuery<
    WorkloadCalendarRespSuccess,
    ApiError
  >({
    queryKey:
      type !== 'day'
        ? [...queryKeys.workloadWeeklyCalendar, queryParams]
        : [...queryKeys.workloadDaily, queryParams],
    queryFn: async () =>
      await queryFetch({
        url: `workloads/calendar${queryParams}`,
      }),
    placeholderData: (previousData) => previousData as WorkloadCalendarRespSuccess,
    enabled: !type || ['week', 'day'].includes(type),
  });

  const { filterParams, filterKey } = useWorkloadFilterParams();
  const windowDates = type === 'week' ? weekDates : undefined;
  const windowKey = windowDates?.map((day) => formatDate(day)).join('_') || '';

  const filtered = useMemo(
    () =>
      filterWorkloadWeeklyRecords(
        data?.records || [],
        data?.withoutTeam || [],
        filterParams,
        windowDates,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.records, data?.withoutTeam, filterKey, windowKey],
  );

  const filteredAllDates = useMemo(
    () =>
      windowDates
        ? filterWorkloadWeeklyRecords(data?.records || [], data?.withoutTeam || [], filterParams)
        : filtered,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.records, data?.withoutTeam, filterKey, windowKey, filtered],
  );

  const drafts = useMemo(
    () => computeWeeklyDrafts(filtered.visibleForemen),
    [filtered.visibleForemen],
  );

  return {
    data,
    visibleRecords: filtered.visibleForemen,
    visibleWithoutTeam: filtered.visibleWithoutTeam,
    allDatesVisibleRecords: filteredAllDates.visibleForemen,
    allDatesVisibleWithoutTeam: filteredAllDates.visibleWithoutTeam,
    hiddenCount: filtered.hiddenCount,
    hiddenForemenCount: filtered.hiddenForemenCount,
    drafts,
    error,
    isLoading: isFetching && (isPending || isPlaceholderData),
  };
};
